import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import {
  brokeragesApi,
  BROKERAGE_STATUSES,
  BROKERAGE_PIPELINE_STATUSES,
  BROKERAGE_STATUS_BADGE,
  type BrokerageStatus,
  type BrokerageSummaryDto,
} from "@/api/brokerages";
import { brokerageKeys } from "@/hooks/brokerages/brokerageKeys";
import { Badge, Button, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { Pagination } from "@/components/layout/Pagination";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";

const PAGE_SIZE = 25;
type View = "table" | "pipeline";

export function BrokeragesPage() {
  const { date } = useDateFormatter();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("Brokerages.Create");

  const [view, setView] = useState<View>("table");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<BrokerageStatus | "">("");
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: ["brokerages", search, status, page],
    queryFn: () =>
      brokeragesApi.list({
        page,
        pageSize: PAGE_SIZE,
        q: search || undefined,
        status: status || undefined,
      }),
    enabled: view === "table",
  });

  const pipeline = useQuery({
    queryKey: brokerageKeys.pipeline(),
    queryFn: () => brokeragesApi.pipeline(),
    enabled: view === "pipeline",
  });

  const totalPages = Math.max(1, Math.ceil((list.data?.totalCount ?? 0) / PAGE_SIZE));

  const columns: Column<BrokerageSummaryDto>[] = useMemo(() => [
    {
      key: "name",
      header: "Name",
      render: (b) => (
        <Link to={`/brokerages/${b.id}`} className="font-semibold hover:text-gold-shadow">
          {b.name}
          {b.dba && <span className="text-caption text-gray-mid font-normal"> — DBA {b.dba}</span>}
        </Link>
      ),
    },
    { key: "phone", header: "Phone", render: (b) => b.phone ?? "—" },
    { key: "brokers", header: "# Brokers", render: (b) => b.brokerCount },
    { key: "leads", header: "# Leads", render: (b) => b.leadCount },
    {
      key: "status",
      header: "Status",
      render: (b) => <Badge status={BROKERAGE_STATUS_BADGE[b.status]}>{b.status}</Badge>,
    },
    {
      key: "updated",
      header: "Updated",
      render: (b) => date(b.modifiedAt ?? b.createdAt),
    },
  ], [date]);

  return (
    <>
      <PageHeader
        eyebrow="Partners"
        title="Brokerages"
        actions={
          canCreate && (
            <Link to="/brokerages/new">
              <Button size="sm">
                <Plus size={14} strokeWidth={1.5} aria-hidden /> New brokerage
              </Button>
            </Link>
          )
        }
      />

      <div className="flex items-center gap-space-2 mb-space-3">
        <div className="inline-flex border border-border-light rounded-md overflow-hidden shrink-0">
          <ViewTab active={view === "table"} onClick={() => setView("table")}>Table</ViewTab>
          <ViewTab active={view === "pipeline"} onClick={() => setView("pipeline")}>Pipeline</ViewTab>
        </div>
        {view === "table" && (
          <>
            <Input
              value={search}
              placeholder="Search by name…"
              className="grow"
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
            <Select
              value={status}
              className="max-w-[12rem] shrink-0"
              onChange={(e) => { setStatus(e.target.value as BrokerageStatus | ""); setPage(1); }}
            >
              <option value="">All statuses</option>
              {BROKERAGE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </>
        )}
      </div>

      {view === "table" ? (
        <>
          <DataTable
            columns={columns}
            rows={list.data?.data ?? []}
            loading={list.isLoading}
            error={list.isError ? (list.error as Error).message : null}
            emptyMessage="No brokerages yet."
            rowKey={(b) => b.id}
          />

          {list.data && list.data.totalCount > 0 && (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              totalCount={list.data.totalCount}
              totalPages={totalPages}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            />
          )}
        </>
      ) : (
        <BrokeragePipelineBoard
          stages={pipeline.data?.stages}
          loading={pipeline.isLoading}
          error={pipeline.isError ? (pipeline.error as Error).message : null}
        />
      )}
    </>
  );
}

function ViewTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-space-3 py-space-1 text-small font-medium transition-colors " +
        (active ? "bg-gold text-onyx" : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10")
      }
    >
      {children}
    </button>
  );
}

// Brokerage Certification & Pipeline — read-only Kanban board over the four
// onboarding-funnel columns. Mirrors the broker pipeline UX; cards link to the
// firm detail page where the stepper advances the stage.
function BrokeragePipelineBoard({
  stages,
  loading,
  error,
}: {
  stages?: Partial<Record<BrokerageStatus, BrokerageSummaryDto[]>>;
  loading: boolean;
  error: string | null;
}) {
  if (loading) return <div className="text-caption uppercase tracking-wide text-gray-mid">Loading pipeline…</div>;
  if (error) return <div className="text-small text-status-error" role="alert">{error}</div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-space-3">
      {BROKERAGE_PIPELINE_STATUSES.map((stage) => {
        const cards = stages?.[stage] ?? [];
        return (
          <div key={stage} className="rounded-md border border-border-light bg-white-warm/40 p-space-2">
            <div className="flex items-center justify-between mb-space-2 px-space-1">
              <span className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
                {stage === "UnderReview" ? "Under review" : stage}
              </span>
              <span className="text-caption text-gray-mid">{cards.length}</span>
            </div>
            <div className="space-y-space-2">
              {cards.map((b) => (
                <Link
                  key={b.id}
                  to={`/brokerages/${b.id}`}
                  className="block rounded-md border border-border-light bg-white-pure p-space-2 hover:border-gold-shadow"
                >
                  <div className="font-semibold text-small text-onyx truncate">{b.name}</div>
                  <div className="text-caption text-gray-mid">
                    {b.brokerCount} broker(s) · {b.leadCount} lead(s)
                  </div>
                </Link>
              ))}
              {cards.length === 0 && (
                <div className="text-caption text-gray-mid italic px-space-1 py-space-2">None</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
